import React, { useState, type FC } from 'react'
import { useMutation } from '@apollo/client'
import FundingRequestDetails from './FundingRequestDetails'
import { extractCPDebtIssuanceRecord, extractLoanFromLenderRecord } from '../utils/textValidations'
import type { CPDebtIssuanceRecord, LoanFromLenderRecord, FundingRequestDataWithNotes } from '../utils/types'
import { useUserEmail } from '../../../hooks/useUserEmail'
import ActionButton from '../../../components/action-button'
import { FR_SOFT_DELETE } from '../../../components/graphql/mutations'

interface FundingRequestTabProps {
  fundingRequestData: FundingRequestDataWithNotes
  showTest?: boolean
  refetch: () => void
  onClose: () => void
  fundingRequestADCId: number
  setMessage: (message: string) => void
  setMessageColor: (color: 'red' | 'green' | 'yellow') => void
}

const FundingRequestTab: FC<FundingRequestTabProps> = ({
  fundingRequestData,
  showTest = true,
  fundingRequestADCId,
  setMessage,
  setMessageColor,
  onClose,
  refetch
}) => {
  const [noteText, setNoteText] = useState<string>('')
  const [loading, setLoading] = useState(false)
  const { userEmail } = useUserEmail()

  const [withdrawFR] = useMutation(FR_SOFT_DELETE)

  if (!fundingRequestData) {
    return <p className="p-4">No data available</p>
  }

  const status = fundingRequestData?.fundingRequestDetails?.cpIssuances[0]?.status
  const showWithdraw = status === 'REJECT'

  const debtIssuanceRecord: CPDebtIssuanceRecord = extractCPDebtIssuanceRecord(
    fundingRequestData?.fundingRequestDetails?.fundingRecords
  )
  const loanFromLenderRecord: LoanFromLenderRecord = extractLoanFromLenderRecord(
    fundingRequestData?.fundingRequestDetails?.fundingRecords
  )

  const handleWithdraw = async () => {
    if (!fundingRequestADCId || !userEmail) return

    setLoading(true)
    try {
      const { data } = await withdrawFR({
        variables: {
          fr_id: fundingRequestADCId,
          updated_by: userEmail,
          note: noteText
        }
      })

      if (data?.withdrawFR?.status) {
        setMessage('Funding request withdrawn successfully')
        setMessageColor('green')
        refetch()
        onClose()
      } else {
        setMessage(data?.withdrawFR?.error || 'Withdrawal failed')
        setMessageColor('red')
      }
    } catch (err) {
      setMessage('Error processing withdrawal')
      setMessageColor('red')
    } finally {
      setLoading(false)
    }
  }

  const handleCancel = async () => {
    onClose()
    await Promise.resolve()
  }

  return (
    <div className="p-4">
      <FundingRequestDetails
        data={fundingRequestData}
        debtIssuanceRecord={debtIssuanceRecord}
        loanFromLenderRecord={loanFromLenderRecord}
        isApproval={false}
        noteText={noteText}
        setNoteText={setNoteText}
        isTab={true}
        showTest={showTest}
      />

      {showWithdraw && (
        <div className="flex flex-col items-end mt-4 space-y-2">
          <div className="w-full">
            <ActionButton
              primaryLabel="Confirm Withdraw"
              secondaryLabel="Cancel"
              primaryAction={handleWithdraw}
              secondaryAction={handleCancel}
              primaryLoading={loading}
            />
          </div>
        </div>
      )}
    </div>
  )
}

export default React.memo(FundingRequestTab)
